import React, { useLayoutEffect, useRef, useState } from "react";
import "./RegistrationAgeWheel.css";
import {
  INITIAL_REGISTRATION_AGE,
  MAX_REGISTRATION_AGE,
  MIN_REGISTRATION_AGE,
} from "./registrationAge";

const ROW_HEIGHT = 56;
const AGES = Array.from(
  { length: MAX_REGISTRATION_AGE - MIN_REGISTRATION_AGE + 1 },
  (_, index) => index + MIN_REGISTRATION_AGE
);

const clampAge = (age) => Math.max(MIN_REGISTRATION_AGE, Math.min(MAX_REGISTRATION_AGE, age));

export default function RegistrationAgeWheel({ value, onChange, label }) {
  const initialAge = clampAge(Number(value) || INITIAL_REGISTRATION_AGE);
  const [centerAge, setCenterAge] = useState(initialAge);
  const wheelRef = useRef(null);
  const interactedRef = useRef(false);

  useLayoutEffect(() => {
    if (wheelRef.current) {
      wheelRef.current.scrollTop = (initialAge - MIN_REGISTRATION_AGE) * ROW_HEIGHT;
    }
    // The picker remounts for each registration step; this is its initial position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseAge = (age, behavior = "smooth") => {
    const nextAge = clampAge(age);
    interactedRef.current = true;
    setCenterAge(nextAge);
    onChange(String(nextAge));
    wheelRef.current?.scrollTo({ top: (nextAge - MIN_REGISTRATION_AGE) * ROW_HEIGHT, behavior });
  };

  const handleScroll = (event) => {
    const nextAge = clampAge(Math.round(event.currentTarget.scrollTop / ROW_HEIGHT) + MIN_REGISTRATION_AGE);
    setCenterAge(nextAge);
    if (interactedRef.current && String(nextAge) !== value) {
      onChange(String(nextAge));
    }
  };

  const handleKeyDown = (event) => {
    const increments = { ArrowUp: -1, ArrowDown: 1, PageUp: -10, PageDown: 10 };
    if (event.key === "Home") {
      event.preventDefault();
      chooseAge(MIN_REGISTRATION_AGE);
    } else if (event.key === "End") {
      event.preventDefault();
      chooseAge(MAX_REGISTRATION_AGE);
    } else if (Object.prototype.hasOwnProperty.call(increments, event.key)) {
      event.preventDefault();
      chooseAge(centerAge + increments[event.key]);
    }
  };

  return (
    <div className="vg-age-wheel" data-testid="registration-age-wheel">
      <div className="vg-age-wheel__selection" aria-hidden="true" />
      <div
        ref={wheelRef}
        className="vg-age-wheel__scroll"
        role="listbox"
        aria-label={label}
        tabIndex={0}
        onScroll={handleScroll}
        onTouchStart={() => { interactedRef.current = true; }}
        onWheel={() => { interactedRef.current = true; }}
        onKeyDown={handleKeyDown}
      >
        {AGES.map((age) => {
          const distance = Math.abs(age - centerAge);
          return (
            <div
              key={age}
              role="option"
              aria-selected={String(age) === value}
              className="vg-age-wheel__option"
              data-distance={Math.min(distance, 3)}
              onClick={() => chooseAge(age)}
            >
              {age}
            </div>
          );
        })}
      </div>
    </div>
  );
}
